import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { Panel, Switch, Button, SectionLabel, Tabs, MetaRow, useToast } from '@/ui';
import { quietScrollbar } from '@/theme/scrollbar';
import { usePreferences } from '@/hooks';
import type { TextSizeKey, BackdropKey, AccentKey, FinishKey } from '@/core/models';
import {
  PANEL_SOLIDITY_RANGE,
  BG_INTENSITY_RANGE,
  GLASS_BLUR_RANGE,
  GLASS_TINT_RANGE,
  GLASS_SATURATION_RANGE,
  RADIUS_PX_RANGE,
  clampRadiusHard,
  clampRadiusBounds,
} from '@/core/models/preferences';
import { ACCENT_PRESETS } from '@/theme/presets/accents';
import { darkTheme, lightTheme } from '@/theme/tokens';
import {
  CARD_PRESETS,
  CARD_PRESET_KEYS,
  presetValues,
  matchPreset,
} from '@/theme/presets/cardPresets';
import { resolveMaterial, grainLabel } from '@/theme/presets/cardMaterial';
import { solveBand, resolveCardSurface, effectiveLuminance } from '@/theme/presets/cardSurface';
import { relativeLuminance, contrastRatioL } from '@/theme/presets/color';
import { writeBackdropLatch } from '@/app/shell/Backdrop/resolveTier';
import { SavedLooks } from '../AppearanceLookPicker';
import { FlairPicker } from '../FlairPicker';
import {
  APPEARANCE_KEYS,
  ALL_APPEARANCE_KEYS,
  type AppearanceCategory,
} from '../appearanceCategories';
import { useAppearanceReset } from '../useAppearanceReset';
import { AccountMenuSettings } from '../AccountMenuSettings';

/* Accent presets shown as swatches (excludes 'custom' — that's the colour wheel). */
const ACCENT_OPTIONS: { key: Exclude<AccentKey, 'custom'>; label: string }[] = [
  { key: 'default', label: 'Purple' },
  { key: 'teal', label: 'Teal' },
  { key: 'sunset', label: 'Sunset' },
  { key: 'aurora', label: 'Aurora' },
  { key: 'ember', label: 'Ember' },
  { key: 'mint', label: 'Mint' },
  { key: 'magma', label: 'Magma' },
];

const FINISH_OPTIONS: { key: FinishKey; label: string }[] = [
  { key: 'flat', label: 'Flat' },
  { key: 'metallic', label: 'Metallic' },
  { key: 'glow', label: 'Glow' },
];

/* The stored `panelSolidity` pref is card *opacity* (higher = more solid). The UI
   presents it as *transparency* (drag right = more see-through) by reflecting
   across the range midpoint — the transform is its own inverse. */
const SOLIDITY_SPAN = PANEL_SOLIDITY_RANGE.min + PANEL_SOLIDITY_RANGE.max;
const flipSolidity = (v: number): number => SOLIDITY_SPAN - v;

/* ─── Layout ─────────────────────────────────────────────────────────────── */

const APPEARANCE_TABS = [
  { value: 'theme', label: 'Theme & Accent' },
  { value: 'cards', label: 'Cards & Colour' },
  { value: 'material', label: 'Material' },
  { value: 'glass', label: 'Glass & Shape' },
  { value: 'text', label: 'Text & Flair' },
  { value: 'motion', label: 'Motion & Background' },
];

const CategoryNav = styled.div`
  min-width: 0;
  margin-bottom: 20px;
  [role='tablist'] {
    overflow-x: auto;
    padding: 2px 0 6px;
    mask-image: none;
    -webkit-mask-image: none;
    ${quietScrollbar}
    &::-webkit-scrollbar {
      display: block;
    }
  }
  [role='tab']:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

const CategoryPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  &[hidden] {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 4px;
  }
`;

const PreviewSlot = styled.div`
  min-width: 0;
  position: sticky;
  top: 20px;
  @media (max-width: 900px) {
    position: static;
  }
`;

const ResetRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Cards = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Ctl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CtlLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

/** Text-size segment labels: the standard S / M / L / XL size scale. */
const TEXT_SIZE_LABELS: Record<TextSizeKey, string> = { sm: 'S', md: 'M', lg: 'L', xl: 'XL' };

/* ─── Control primitives ─────────────────────────────────────────────────── */

/** Generic segmented button row for text/radius/font/backdrop pickers. */
const SegmentGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  width: fit-content;
  max-width: 100%;
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  background: ${({ $active, theme }) => ($active ? theme.color.glassBorder : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: -2px;
  }
`;

/** Range input styled to use the theme accent color. */
const RangeInput = styled.input`
  appearance: none;
  width: 200px;
  min-width: 0;
  max-width: 100%;
  margin-inline: 0;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.color.glassBorder};
  accent-color: ${({ theme }) => theme.color.brandFrom};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 4px;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

/** Mono value readout shown beside a slider (e.g. "60%", "12px"). */
const Val = styled.span`
  flex-shrink: 0;
  min-width: 46px;
  text-align: right;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Wraps the glass sliders; dims + disables interaction when glass is off. */
const GlassControls = styled.div<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: opacity 0.15s;
  opacity: ${({ $on }) => ($on ? 1 : 0.4)};
  pointer-events: ${({ $on }) => ($on ? 'auto' : 'none')};
`;

/** A slider paired with its live value readout (right-aligned control cluster). */
const SliderCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  /* The radius row includes two 48px bounds and a readout. Let only its
     slider shrink instead of imposing the whole row's intrinsic width. */
  min-width: 0;
  max-width: 100%;
`;

/* Accent swatch row + custom colour wheel. */
const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Swatch = styled.button<{ $c: string; $active: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 0;
  cursor: pointer;
  background: ${({ $c }) => $c};
  border: 2px solid ${({ $active, theme }) => ($active ? theme.color.textPrimary : 'transparent')};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.color.glassBorder};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

/** The custom-colour control: a native colour input in a hairline square. The
    input paints its own live value, so the swatch is always the real colour. */
const Wheel = styled.label<{ $active: boolean }>`
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.sm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  padding: 2px;
  box-shadow: 0 0 0 2px
    ${({ $active, theme }) => ($active ? theme.color.textPrimary : 'transparent')};
  input {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: ${({ theme }) => theme.radius.sm};
    padding: 0;
    background: transparent;
    cursor: pointer;
  }
  input::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  input::-webkit-color-swatch {
    border: none;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
  input:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 3px;
  }
`;

const SwatchHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: 4px;
`;

const CautionHint = styled.p`
  margin: -6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.safety};
`;

/** Explanatory copy under a control. Load-bearing for the colour projection. */
const Hint = styled.p`
  margin: -6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Small numeric box flanking the radius slider — sets the slider's own bounds. */
const BoundInput = styled.input`
  width: 48px;
  flex: 0 0 48px;
  padding: 4px 6px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textPrimary};
  background-color: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

/** Live contrast readout — visible proof of the card-colour legibility guarantee. */
const ContrastRow = styled.p`
  margin: -4px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: baseline;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Chip = styled.span<{ $ok: boolean }>`
  color: ${({ $ok, theme }) => ($ok ? theme.color.success : theme.color.safety)};
`;

/* ─── AppearanceSection ──────────────────────────────────────────────────── */

export function AppearanceSection() {
  const [category, setCategory] = useState('theme');
  const tabsId = useId();
  const categoryNavRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function revealSelectedTab() {
      const strip = categoryNavRef.current?.querySelector<HTMLElement>('[role="tablist"]');
      const selected = strip?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!strip || !selected) return;
      const bounds = strip.getBoundingClientRect();
      const tab = selected.getBoundingClientRect();
      if (tab.left < bounds.left) strip.scrollLeft += tab.left - bounds.left;
      else if (tab.right > bounds.right) strip.scrollLeft += tab.right - bounds.right;
    }
    revealSelectedTab();
    window.addEventListener('resize', revealSelectedTab);
    return () => window.removeEventListener('resize', revealSelectedTab);
  }, [category]);
  const { prefs, setPreference, updatePreferences, toggleTheme, themeMode } = usePreferences();
  const resetAppearance = useAppearanceReset();
  const toast = useToast();
  const theme = useTheme();
  const base = themeMode === 'dark' ? darkTheme : lightTheme;

  // Preset swatches resolve against the BASE tokens (not the already-themed ones), so each
  // swatch truthfully shows the colour that preset would land on.
  const band = useMemo(
    () =>
      solveBand(themeMode, base.color.textPrimary, base.color.textMuted, theme.color.accentCyan),
    [themeMode, base, theme.color.accentCyan],
  );
  const swatchFor = (key: (typeof CARD_PRESET_KEYS)[number]): string => {
    const r = CARD_PRESETS[key];
    const mat = resolveMaterial(themeMode, {
      cardSheen: r.sheen,
      cardGrain: r.grain,
      cardEdge: r.edge,
      cardElevation: r.elevation,
    });
    return resolveCardSurface(
      themeMode,
      base.color.surface,
      r.seed || prefs.cardColor,
      r.strength,
      mat,
      band,
    );
  };
  const activePreset = matchPreset(prefs);

  // Live contrast of the REAL rendered card, read off the theme so it can never drift
  // from the projection maths in cardSurface.ts.
  const cardL = effectiveLuminance(theme.color.surface, themeMode, theme.material);
  const ratios = {
    primary: contrastRatioL(relativeLuminance(theme.color.textPrimary), cardL),
    muted: contrastRatioL(relativeLuminance(theme.color.textMuted), cardL),
    accent: contrastRatioL(relativeLuminance(theme.color.accentCyan), cardL),
  };

  function applyPreset(key: (typeof CARD_PRESET_KEYS)[number]) {
    const v = presetValues(key, prefs.cardColor);
    void updatePreferences(v);
    toast(`Applied ${CARD_PRESETS[key].label}`);
  }

  /** Bound boxes set the slider's OWN range; the other bound yields if they'd cross. */
  function setRadiusBound(which: 'min' | 'max', raw: string) {
    const n = Number(raw);
    if (!Number.isFinite(n) || raw.trim() === '') return;
    const [lo, hi] =
      which === 'min'
        ? clampRadiusBounds(n, prefs.radiusMaxPx, 'min')
        : clampRadiusBounds(prefs.radiusMinPx, n, 'max');
    void updatePreferences({
      radiusMinPx: lo,
      radiusMaxPx: hi,
      radiusPx: Math.min(hi, Math.max(lo, prefs.radiusPx)),
    });
  }

  const panels: Record<string, ReactNode> = {
    theme: (
      <>
        {/* ── Theme ────────────────────────────────────────────────────── */}
        <Card>
          <CardTitle>Theme</CardTitle>

          <Ctl>
            <CtlLabel>Dark mode</CtlLabel>
            <Switch
              checked={themeMode === 'dark'}
              onChange={() => toggleTheme()}
              label="Dark mode"
            />
          </Ctl>
        </Card>

        {/* ── Accent & Style ───────────────────────────────────────────── */}
        <Card>
          <CardTitle>Accent &amp; Style</CardTitle>

          <Ctl>
            <CtlLabel>Accent color</CtlLabel>
            <Swatches role="group" aria-label="Accent color">
              {ACCENT_OPTIONS.map(({ key, label }) => (
                <Swatch
                  key={key}
                  type="button"
                  title={label}
                  aria-label={label}
                  aria-pressed={prefs.accent === key}
                  $c={ACCENT_PRESETS[key][themeMode].accentCyan}
                  $active={prefs.accent === key}
                  onClick={() => void setPreference('accent', key)}
                />
              ))}
              <Wheel $active={prefs.accent === 'custom'} title="Custom color (color wheel)">
                <input
                  type="color"
                  aria-label="Custom accent color"
                  value={prefs.accentCustom}
                  onChange={(e) => {
                    void setPreference('accentCustom', e.target.value);
                    void setPreference('accent', 'custom');
                  }}
                />
              </Wheel>
              {prefs.accent === 'custom' && (
                <SwatchHint>{prefs.accentCustom.toUpperCase()}</SwatchHint>
              )}
            </Swatches>
          </Ctl>

          <Ctl>
            <CtlLabel title="Applies to accent fills (primary buttons and the accent glow). Cards are configured under Card Appearance.">
              Accent fill
            </CtlLabel>
            <SegmentGroup role="group" aria-label="Accent fill">
              {FINISH_OPTIONS.map(({ key, label }) => (
                <Segment
                  key={key}
                  type="button"
                  $active={prefs.finish === key}
                  aria-pressed={prefs.finish === key}
                  onClick={() => void setPreference('finish', key)}
                >
                  {label}
                </Segment>
              ))}
            </SegmentGroup>
          </Ctl>
        </Card>
      </>
    ),
    cards: (
      <>
        {/* ── Card Appearance ──────────────────────────────────────────── */}
        <Card>
          <CardTitle>Card Appearance</CardTitle>

          <SectionLabel>Overlay gradients</SectionLabel>
          <Ctl>
            <CtlLabel>Colour wash</CtlLabel>
            <SegmentGroup role="group" aria-label="Overlay gradient">
              {(['none', 'accent', 'aurora', 'sunset'] as const).map((key) => (
                <Segment
                  key={key}
                  type="button"
                  $active={prefs.overlayGradient === key}
                  aria-pressed={prefs.overlayGradient === key}
                  onClick={() => void setPreference('overlayGradient', key)}
                >
                  {key === 'none' ? 'Solid' : key.charAt(0).toUpperCase() + key.slice(1)}
                </Segment>
              ))}
            </SegmentGroup>
          </Ctl>
          {(
            [
              ['postGradientStrength', 'Posts & cards'],
              ['panelGradientStrength', 'Side panels'],
              ['overlayGradientStrength', 'Overlays & promotions'],
            ] as const
          ).map(([key, label]) => (
            <Ctl key={key}>
              <CtlLabel>{label}</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  aria-label={`${label} gradient strength`}
                  disabled={prefs.overlayGradient === 'none'}
                  value={prefs[key]}
                  aria-valuetext={`${Math.round(prefs[key] * 100)} percent`}
                  onChange={(e) => void setPreference(key, Number(e.target.value))}
                />
                <Val>{Math.round(prefs[key] * 100)}%</Val>
              </SliderCluster>
            </Ctl>
          ))}
          <Hint>
            Soft colour across cards, menus and composing overlays. Preview changes here; your
            choice is saved for this account on this browser.
          </Hint>

          <SectionLabel>Preset</SectionLabel>
          <Ctl>
            <CtlLabel>Card material</CtlLabel>
            <Swatches role="group" aria-label="Card material preset">
              {CARD_PRESET_KEYS.map((key) => (
                <Swatch
                  key={key}
                  type="button"
                  title={CARD_PRESETS[key].label}
                  aria-label={CARD_PRESETS[key].label}
                  aria-pressed={activePreset === key}
                  $c={swatchFor(key)}
                  $active={activePreset === key}
                  onClick={() => applyPreset(key)}
                />
              ))}
            </Swatches>
          </Ctl>
          <Hint>
            Presets adjust both colour and material. Fine-tune these in Cards &amp; Colour and
            Material.
          </Hint>

          <SectionLabel>Colour</SectionLabel>
          <Ctl>
            <CtlLabel>Card colour</CtlLabel>
            <Swatches>
              <Wheel $active={prefs.cardColorStrength > 0} title="Custom card colour">
                <input
                  type="color"
                  aria-label="Card colour"
                  value={prefs.cardColor}
                  onChange={(e) => {
                    void setPreference('cardColor', e.target.value);
                    // Make a pick visible immediately rather than silently inert.
                    if (prefs.cardColorStrength === 0) void setPreference('cardColorStrength', 0.6);
                  }}
                />
              </Wheel>
              <SwatchHint>{theme.color.surface.toUpperCase()}</SwatchHint>
              <Button variant="ghost" onClick={() => void setPreference('cardColorStrength', 0)}>
                Theme default
              </Button>
            </Swatches>
          </Ctl>

          <Ctl>
            <CtlLabel>Colour strength</CtlLabel>
            <SliderCluster>
              <RangeInput
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={prefs.cardColorStrength}
                aria-label="Card colour strength"
                aria-valuetext={`${Math.round(prefs.cardColorStrength * 100)} percent`}
                onChange={(e) => void setPreference('cardColorStrength', Number(e.target.value))}
              />
              <Val>{Math.round(prefs.cardColorStrength * 100)}%</Val>
            </SliderCluster>
          </Ctl>

          <ContrastRow role="status" aria-live="polite">
            <span>
              Body <Chip $ok={ratios.primary >= 4.5}>{ratios.primary.toFixed(1)}:1</Chip>
            </span>
            <span>
              Muted <Chip $ok={ratios.muted >= 4.5}>{ratios.muted.toFixed(1)}:1</Chip>
            </span>
            <span>
              Accent <Chip $ok={ratios.accent >= 3}>{ratios.accent.toFixed(1)}:1</Chip>
            </span>
            <span>measured over the app background</span>
          </ContrastRow>
          <Hint>
            Card colour sets hue and saturation. Lightness is fixed by your theme so text stays
            readable.
          </Hint>
          {!band.accentEnforced && (
            <CautionHint>
              Your custom accent is low-contrast on this card colour. Links and icons may be hard to
              read.
            </CautionHint>
          )}
        </Card>
      </>
    ),
    material: (
      <>
        <Card>
          <CardTitle>Material</CardTitle>
          <Ctl>
            <CtlLabel>Sheen</CtlLabel>
            <SliderCluster>
              <RangeInput
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={prefs.cardSheen}
                aria-label="Card sheen"
                aria-valuetext={`${Math.round(prefs.cardSheen * 100)} percent`}
                onChange={(e) => void setPreference('cardSheen', Number(e.target.value))}
              />
              <Val>{Math.round(prefs.cardSheen * 100)}%</Val>
            </SliderCluster>
          </Ctl>

          <Ctl>
            <CtlLabel>Grain</CtlLabel>
            <SliderCluster>
              <RangeInput
                type="range"
                min={0}
                max={1}
                step={0.25}
                value={prefs.cardGrain}
                aria-label="Card grain"
                aria-valuetext={grainLabel(prefs.cardGrain)}
                onChange={(e) => void setPreference('cardGrain', Number(e.target.value))}
              />
              <Val>{grainLabel(prefs.cardGrain)}</Val>
            </SliderCluster>
          </Ctl>

          {/* Edge light is gated behind Sheen — a specular edge on a matte card is the
              classic fake-bevel tell, so it dims out exactly like the glass group. */}
          <GlassControls $on={prefs.cardSheen > 0} aria-hidden={prefs.cardSheen === 0}>
            <Ctl>
              <CtlLabel>Edge light</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={prefs.cardEdge}
                  disabled={prefs.cardSheen === 0}
                  aria-label="Card edge light"
                  aria-valuetext={`${Math.round(prefs.cardEdge * 100)} percent`}
                  onChange={(e) => void setPreference('cardEdge', Number(e.target.value))}
                />
                <Val>{Math.round(prefs.cardEdge * 100)}%</Val>
              </SliderCluster>
            </Ctl>
          </GlassControls>

          <Ctl>
            <CtlLabel>Elevation</CtlLabel>
            <SliderCluster>
              <RangeInput
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={prefs.cardElevation}
                aria-label="Card elevation"
                aria-valuetext={`${Math.round(prefs.cardElevation * 100)} percent`}
                onChange={(e) => void setPreference('cardElevation', Number(e.target.value))}
              />
              <Val>{Math.round(prefs.cardElevation * 100)}%</Val>
            </SliderCluster>
          </Ctl>
        </Card>
      </>
    ),
    glass: (
      <>
        <Card>
          <CardTitle>Glass &amp; Shape</CardTitle>
          <SectionLabel>Corners</SectionLabel>
          <Ctl>
            <CtlLabel title="Also scales buttons, inputs and popovers. This is the global shape scale.">
              Corner radius
            </CtlLabel>
            <SliderCluster>
              <BoundInput
                type="number"
                value={prefs.radiusMinPx}
                min={RADIUS_PX_RANGE.hardMin}
                max={RADIUS_PX_RANGE.hardMax}
                aria-label="Corner radius slider minimum"
                title="Slider bound, 0–48px."
                onChange={(e) => setRadiusBound('min', e.target.value)}
              />
              <RangeInput
                type="range"
                min={prefs.radiusMinPx}
                max={prefs.radiusMaxPx}
                step={1}
                value={prefs.radiusPx}
                aria-label="Corner radius"
                aria-valuetext={`${prefs.radiusPx} pixels`}
                onChange={(e) =>
                  void setPreference('radiusPx', clampRadiusHard(Number(e.target.value)))
                }
              />
              <BoundInput
                type="number"
                value={prefs.radiusMaxPx}
                min={RADIUS_PX_RANGE.hardMin}
                max={RADIUS_PX_RANGE.hardMax}
                aria-label="Corner radius slider maximum"
                title="Slider bound, 0–48px."
                onChange={(e) => setRadiusBound('max', e.target.value)}
              />
              <Val>{prefs.radiusPx}px</Val>
            </SliderCluster>
          </Ctl>
          <Hint>Boxes set the slider&apos;s own range (0–48px).</Hint>

          <SectionLabel>Glass</SectionLabel>
          <Ctl>
            <CtlLabel>Glass cards (show background through)</CtlLabel>
            <Switch
              checked={!prefs.cardsOverlay}
              onChange={(v) => void setPreference('cardsOverlay', !v)}
              label="Glass cards"
            />
          </Ctl>

          <GlassControls $on={!prefs.cardsOverlay} aria-hidden={prefs.cardsOverlay}>
            <Ctl>
              <CtlLabel>Transparency</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={PANEL_SOLIDITY_RANGE.min}
                  max={PANEL_SOLIDITY_RANGE.max}
                  step={0.01}
                  value={flipSolidity(prefs.panelSolidity)}
                  disabled={prefs.cardsOverlay}
                  aria-label="Card transparency"
                  aria-valuetext={`${Math.round((1 - prefs.panelSolidity) * 100)} percent transparent`}
                  onChange={(e) =>
                    void setPreference('panelSolidity', flipSolidity(Number(e.target.value)))
                  }
                />
                <Val>{Math.round((1 - prefs.panelSolidity) * 100)}%</Val>
              </SliderCluster>
            </Ctl>

            <Ctl>
              <CtlLabel>Blur</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={GLASS_BLUR_RANGE.min}
                  max={GLASS_BLUR_RANGE.max}
                  step={1}
                  value={prefs.glassBlur}
                  disabled={prefs.cardsOverlay}
                  aria-label="Glass blur"
                  aria-valuetext={`${prefs.glassBlur} pixels`}
                  onChange={(e) => void setPreference('glassBlur', Number(e.target.value))}
                />
                <Val>{prefs.glassBlur}px</Val>
              </SliderCluster>
            </Ctl>

            <Ctl>
              <CtlLabel>Accent tint</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={GLASS_TINT_RANGE.min}
                  max={GLASS_TINT_RANGE.max}
                  step={0.02}
                  value={prefs.glassTint}
                  disabled={prefs.cardsOverlay}
                  aria-label="Glass accent tint"
                  aria-valuetext={`${Math.round(prefs.glassTint * 100)} percent`}
                  onChange={(e) => void setPreference('glassTint', Number(e.target.value))}
                />
                <Val>{Math.round(prefs.glassTint * 100)}%</Val>
              </SliderCluster>
            </Ctl>

            <Ctl>
              <CtlLabel>Saturation</CtlLabel>
              <SliderCluster>
                <RangeInput
                  type="range"
                  min={GLASS_SATURATION_RANGE.min}
                  max={GLASS_SATURATION_RANGE.max}
                  step={0.05}
                  value={prefs.glassSaturation}
                  disabled={prefs.cardsOverlay}
                  aria-label="Glass saturation"
                  aria-valuetext={`${Math.round(prefs.glassSaturation * 100)} percent`}
                  onChange={(e) => void setPreference('glassSaturation', Number(e.target.value))}
                />
                <Val>{prefs.glassSaturation.toFixed(2)}×</Val>
              </SliderCluster>
            </Ctl>
          </GlassControls>
        </Card>
      </>
    ),
    text: (
      <>
        {/* ── Typography ───────────────────────────────────────────────── */}
        <Card>
          <CardTitle>Typography</CardTitle>

          <Ctl>
            <CtlLabel>Text size</CtlLabel>
            <SegmentGroup role="group" aria-label="Text size">
              {(['sm', 'md', 'lg', 'xl'] as TextSizeKey[]).map((key) => (
                <Segment
                  key={key}
                  type="button"
                  $active={prefs.textSize === key}
                  aria-pressed={prefs.textSize === key}
                  onClick={() => void setPreference('textSize', key)}
                >
                  {TEXT_SIZE_LABELS[key]}
                </Segment>
              ))}
            </SegmentGroup>
          </Ctl>
        </Card>

        {/* ── Profile flair (Pro) ──────────────────────────────────────── */}
        <FlairPicker />
      </>
    ),
    motion: (
      <>
        {/* ── Motion & Surfaces ────────────────────────────────────────── */}
        <Card>
          <CardTitle>Motion &amp; Background</CardTitle>

          <Ctl>
            <CtlLabel>Background</CtlLabel>
            <SegmentGroup role="group" aria-label="Background quality">
              {(['auto', 'high', 'low', 'off'] as BackdropKey[]).map((key) => (
                <Segment
                  key={key}
                  type="button"
                  $active={prefs.backdrop === key}
                  aria-pressed={prefs.backdrop === key}
                  onClick={() => {
                    // An explicit choice is a statement of intent — clear any
                    // watchdog latch so the pick takes effect immediately.
                    writeBackdropLatch(null);
                    void setPreference('backdrop', key);
                  }}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Segment>
              ))}
            </SegmentGroup>
          </Ctl>

          <Ctl>
            <CtlLabel>Background intensity</CtlLabel>
            <RangeInput
              type="range"
              min={BG_INTENSITY_RANGE.min}
              max={BG_INTENSITY_RANGE.max}
              step={0.05}
              value={prefs.bgIntensity}
              aria-label="Background intensity"
              aria-valuetext={`${Math.round(prefs.bgIntensity * 100)} percent`}
              onChange={(e) => void setPreference('bgIntensity', Number(e.target.value))}
            />
          </Ctl>
          {prefs.bgIntensity > BG_INTENSITY_RANGE.legibleMax && (
            <CautionHint>Bolder than recommended. The sun may reduce feed legibility.</CautionHint>
          )}

          <Ctl>
            <CtlLabel>
              <MetaRow>
                <span>Banner Animations</span>
                <span>Experimental</span>
              </MetaRow>
            </CtlLabel>
            <Switch
              checked={prefs.bannerAnimations}
              onChange={(v) => void setPreference('bannerAnimations', v)}
              label="Banner Animations"
            />
          </Ctl>
          <Hint>
            Gentle floating shapes and glowing artwork in the Feedback banner. Off by default.
            Reduce motion takes priority.
          </Hint>
          <Ctl>
            <CtlLabel>Reduce motion</CtlLabel>
            <Switch
              checked={prefs.reduceMotion}
              onChange={(v) => void setPreference('reduceMotion', v)}
              label="Reduce motion"
            />
          </Ctl>
        </Card>
      </>
    ),
  };

  return (
    <>
      <CategoryNav ref={categoryNavRef}>
        <Tabs
          items={APPEARANCE_TABS}
          value={category}
          onChange={setCategory}
          ariaLabel="Appearance categories"
          idPrefix={tabsId}
        />
      </CategoryNav>
      <Grid>
        <Cards>
          {APPEARANCE_TABS.map(({ value, label }) => (
            <CategoryPanel
              key={value}
              role="tabpanel"
              id={`${tabsId}-panel-${value}`}
              aria-labelledby={`${tabsId}-tab-${value}`}
              hidden={category !== value}
              tabIndex={0}
            >
              {panels[value]}
              <ResetRow>
                <Button
                  variant="ghost"
                  onClick={() =>
                    resetAppearance(
                      APPEARANCE_KEYS[value as AppearanceCategory],
                      value === 'text' ? 'Text settings' : label,
                    )
                  }
                >
                  {value === 'text' ? 'Reset text settings' : 'Reset this category'}
                </Button>
              </ResetRow>
              {value === 'text' && (
                <Hint>
                  Avatar ring is saved to your account separately. Use No ring to remove it.
                </Hint>
              )}
            </CategoryPanel>
          ))}
          <ResetRow>
            <Button
              variant="ghost"
              onClick={() => {
                resetAppearance(ALL_APPEARANCE_KEYS, 'All appearance');
              }}
            >
              Reset all appearance
            </Button>
          </ResetRow>
        </Cards>
        <PreviewSlot>
          <SavedLooks />
        </PreviewSlot>
      </Grid>
      <AccountMenuSettings />
    </>
  );
}
